import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { fonts as defaultFonts } from "../styles/theme";
import { submitPublicSignUp } from "../services/api";

const PAGE = {
  bg: "#000925",
  bgGradientEnd: "#000f2d",
  card: "#0a1a3d",
  border: "#1a2744",
  text: "#ffffff",
  textSecondary: "#6b7a94",
  inputBg: "#000f2d",
};

const INDUSTRIES = [
  "Asset Management",
  "Banking & Financial Services",
  "Commodities & Energy",
  "Consulting",
  "Defence & Security",
  "Government & Public Sector",
  "Insurance",
  "Legal",
  "Media & Publishing",
  "Mining & Natural Resources",
  "Private Equity & Venture Capital",
  "Real Estate",
  "Technology",
  "Other",
];

export default function SignUp() {
  const tenant = useTenant();
  const fonts = tenant.theme?.fonts || defaultFonts;
  const brandBlue = "#4E95D9";

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    industry: "",
    email: "",
    phone: "",
    country: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.firstName.trim()) return setError("First name is required");
    if (!form.lastName.trim()) return setError("Family name is required");
    if (!form.email.trim()) return setError("Email is required");
    if (!form.country.trim()) return setError("Country is required");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim()))
      return setError("Please enter a valid email address");

    setSubmitting(true);
    try {
      await submitPublicSignUp(form);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    fontSize: 14,
    fontFamily: fonts.body,
    border: `1px solid ${PAGE.border}`,
    borderRadius: 8,
    background: PAGE.inputBg,
    color: PAGE.text,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    color: PAGE.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 6,
    fontFamily: fonts.body,
    fontWeight: 600,
  };

  return (
    <div
      style={{
        height: "100vh",
        overflowY: "auto",
        background: `linear-gradient(180deg, ${PAGE.bg} 0%, ${PAGE.bgGradientEnd} 100%)`,
        fontFamily: fonts.body,
        color: PAGE.text,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px",
      }}
    >
      {/* Logo header */}
      <div style={{ marginBottom: 48, textAlign: "center" }}>
        <img
          src={tenant.logo}
          alt={tenant.name}
          style={{ height: 160, marginBottom: 20, objectFit: "contain" }}
        />
        <div
          style={{
            fontSize: 15,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: brandBlue,
            fontWeight: 700,
          }}
        >
          {tenant.companyName}
        </div>
      </div>

      {/* Main card */}
      <div
        style={{
          background: PAGE.card,
          border: `1px solid ${PAGE.border}`,
          borderRadius: 16,
          padding: 40,
          width: "100%",
          maxWidth: 520,
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        {success ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CheckCircle2
              size={40}
              color="#10b981"
              style={{ marginBottom: 16 }}
            />
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 22,
                marginBottom: 8,
              }}
            >
              Thank You!
            </h2>
            <p
              style={{
                color: PAGE.textSecondary,
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              Your details have been received. A member of our team will be in
              touch shortly.
            </p>
          </div>
        ) : (
          <>
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 22,
                marginBottom: 4,
                textAlign: "center",
              }}
            >
              Sign Up
            </h2>
            <p
              style={{
                color: PAGE.textSecondary,
                fontSize: 13,
                textAlign: "center",
                marginBottom: 28,
              }}
            >
              Register your interest to learn more about our services
            </p>

            <form onSubmit={handleSubmit}>
              {/* Name row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div>
                  <label style={labelStyle}>
                    First Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={set("firstName")}
                    placeholder="John"
                    style={inputStyle}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    Family Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={set("lastName")}
                    placeholder="Smith"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  Email <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="john@company.com"
                  style={inputStyle}
                  required
                />
              </div>

              {/* Phone */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+44 20 7123 4567"
                  style={inputStyle}
                />
              </div>

              {/* Company + Industry row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div>
                  <label style={labelStyle}>Company</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Acme Corp"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Industry</label>
                  <select
                    value={form.industry}
                    onChange={set("industry")}
                    style={{
                      ...inputStyle,
                      appearance: "none",
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%236b7a94' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      paddingRight: 32,
                    }}
                  >
                    <option value="">Select...</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind} value={ind}>
                        {ind}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Country (required for GDPR) */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  Country <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.country}
                  onChange={set("country")}
                  placeholder="United Kingdom"
                  style={inputStyle}
                  required
                />
              </div>

              {/* GDPR consent notice */}
              <div
                style={{
                  fontSize: 11,
                  color: PAGE.textSecondary,
                  lineHeight: 1.5,
                  marginBottom: 20,
                  padding: "12px 14px",
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${PAGE.border}`,
                  borderRadius: 6,
                }}
              >
                By submitting this form, you consent to Fordham Global Foresight
                Group Ltd. storing and processing your personal data for the
                purpose of contacting you about our services. Your data will be
                handled in accordance with the UK GDPR and the Data Protection
                Act 2018. You may withdraw your consent or request deletion of
                your data at any time by contacting{" "}
                <a
                  href="mailto:info@fordhamglobal.com"
                  style={{ color: PAGE.textSecondary, textDecoration: "underline" }}
                >
                  info@fordhamglobal.com
                </a>.
              </div>

              {error && (
                <div
                  style={{
                    padding: "10px 14px",
                    marginBottom: 16,
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    color: "#ef4444",
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  background: brandBlue,
                  color: "#0a0c10",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: fonts.body,
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {submitting && (
                  <Loader2
                    size={16}
                    className="spin"
                  />
                )}
                {submitting ? "Submitting..." : "Sign Up"}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 40,
          textAlign: "center",
          color: PAGE.textSecondary,
          fontSize: 11,
        }}
      >
        &copy; {new Date().getFullYear()} {tenant.legalName} All rights
        reserved.
      </div>    </div>
  );
}
